"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const core_services_auth_sso_detector_1 = require("@msteams/core-services-auth-sso-detector");
const TelemetryScenarioTracker_1 = require("./TelemetryScenarioTracker");
const ConfigurationService_1 = require("./ConfigurationService");
/**
 * Build the redirect URI matching Teams' own registered URI for the current
 * origin. The Teams web client (react-web-client experience) registers
 * `${origin}/v2/authv2` against the Teams AAD app — see
 * teams-modular-packages exp-configs/react-web-client/auth/auth.json
 * (`redirectUrlPath` = "v2") and the msal2 provider which appends "/authv2"
 * (the COOP redirect bridge page) to build the final redirectUri. Join
 * Launcher and Teams share the `teams.microsoft.com` origin (and
 * `teams.live.com` for Life), so the same URI is reachable from a hidden
 * iframe.
 *
 * Same-origin is required because the SSO detector reads
 * `iframe.contentWindow.location.hash` to extract the token response.
 */
function getTeamsRedirectUri() {
    return `${window.location.origin}/v2/authv2`;
}
class AuthSsoDetectorService {
    constructor() {
        this.status = "skipped";
    }
    /**
     * Begin the SSO probe. Idempotent — subsequent calls return the same
     * promise. Caller is responsible for the feature-flag gate; if the flag
     * is off, call `markDisabled()` instead so telemetry/page_loaded report
     * "disabled" rather than "skipped".
     */
    start() {
        if (this.resultPromise) {
            return this.resultPromise;
        }
        this.status = "pending";
        const scenario = TelemetryScenarioTracker_1.TelemetryScenarioTracker.getOrCreateScenario(TelemetryScenarioTracker_1.ScenarioNames.TeamsSsoDetection);
        scenario.start();
        const detector = new core_services_auth_sso_detector_1.CoreServicesAuthSsoDetector({
            clientId: ConfigurationService_1.configurationService.getConfig().teamsClientId,
            redirectUri: getTeamsRedirectUri(),
            authority: ConfigurationService_1.configurationService.getConfig().ssoAuthority,
        });
        this.resultPromise = detector.getSSOAvailabilityWithPerf().then(({ isSSOAvailable, timeMs }) => {
            this.status = isSSOAvailable ? "available" : "unavailable";
            // Single stop emission with ssoStatus + ssoStatusTimeMs in
            // payload. Calling markStep(Stop, …) followed by stop() would
            // emit two scenario rows with Scenario_Step="stop" at the same
            // timestamp (one with the payload, one without), because
            // markStep is intermediate-only and does not flip isCompleted.
            scenario.stop({
                ssoStatus: this.status,
                ssoStatusTimeMs: timeMs,
            });
            return isSSOAvailable;
        }, () => {
            this.status = "error";
            scenario.fail("detector_threw");
            return false;
        });
        return this.resultPromise;
    }
    markDisabled() {
        this.status = "disabled";
    }
    getStatus() {
        return this.status;
    }
    getResultPromise() {
        return this.resultPromise;
    }
    // Test-only helper. Resets the singleton so each spec runs in isolation.
    _resetForTesting() {
        this.status = "skipped";
        this.resultPromise = undefined;
    }
}
const authSsoDetectorService = new AuthSsoDetectorService();
exports.default = authSsoDetectorService;
